/**
 * chartSort.ts — bar order for the bar-family charts (#1808).
 *
 * One pure helper the six bar-family generators and the PPTX export share.
 * Every function returns READING order: top-down on the horizontal types
 * (which draw with `yAxis.inverse: true` when a sort is set), left to right
 * on the vertical ones.
 *
 * Absent sort_order is not handled here: the generators keep their own
 * pre-#1808 code path for it (bar ascending with the biggest bar drawn on
 * top, column descending, the clustered and stacked types in table order),
 * so a saved chart never moves.
 */

import type { ChartSortOrder } from '../../../types/gotypes'
import type { ChartType, ParsedTable } from '../types'
import { toNumber } from '../generators/helpers'

export type { ChartSortOrder }

export const BAR_FAMILY_CHART_TYPES = [
  'bar',
  'column',
  'clusteredBar',
  'stackedBar',
  'clusteredColumn',
  'stackedColumn',
] as const

export function isBarFamilyChartType(chartType: string | undefined): boolean {
  return (BAR_FAMILY_CHART_TYPES as readonly string[]).includes(chartType || '')
}

/** Horizontal types draw their categories on the Y axis. */
export function isHorizontalBarChartType(chartType: string | undefined): boolean {
  return chartType === 'bar' || chartType === 'clusteredBar' || chartType === 'stackedBar'
}

/**
 * The order a chart with no sort_order has always drawn, named in the
 * editor's terms: bar and column sort by value with the highest first; the
 * clustered and stacked types keep table order.
 */
export function legacySortOrder(chartType: string | undefined): ChartSortOrder {
  return chartType === 'bar' || chartType === 'column' ? 'desc' : 'table'
}

function isNum(v: number | null): v is number {
  return v !== null && !Number.isNaN(v)
}

/**
 * Orders items for a chart. `key` is a cell index (items are table rows of
 * strings) or a function returning the sort value. Items with no numeric
 * value go last in both directions; ties keep table order. Pure.
 */
export function orderRows<T>(
  items: T[],
  key: number | ((item: T) => number | null),
  order: ChartSortOrder,
): T[] {
  if (order === 'table') return [...items]
  const valueOf = typeof key === 'number'
    ? (item: T) => toNumber((item as unknown as string[])[key])
    : key
  const sign = order === 'desc' ? -1 : 1
  return items
    .map((item, index) => ({ item, index, value: valueOf(item) }))
    .sort((a, b) => {
      const an = isNum(a.value)
      const bn = isNum(b.value)
      if (an && bn && a.value !== b.value) return sign * ((a.value as number) - (b.value as number))
      if (an !== bn) return an ? -1 : 1
      return a.index - b.index
    })
    .map(({ item }) => item)
}

/** Sum of a row's numeric cells (index 1 on); null when none is numeric. */
export function rowTotal(row: string[]): number | null {
  let total: number | null = null
  for (let i = 1; i < row.length; i++) {
    const v = toNumber(row[i])
    if (isNum(v)) total = (total ?? 0) + v
  }
  return total
}

/**
 * Puts a ParsedTable in reading order for a bar-family chart.
 *
 *  - bar, column, clusteredBar: the bars are ROWS, ordered by the first
 *    value column (for clustered, the first series);
 *  - stackedBar: rows, ordered by the bar's total;
 *  - clusteredColumn: the bars are COLUMNS (rows are series), ordered by the
 *    first series' value in each column;
 *  - stackedColumn: columns, ordered by the column total.
 *
 * 'table' returns the same table object.
 */
export function sortTableForChart(table: ParsedTable, chartType: ChartType, order: ChartSortOrder): ParsedTable {
  if (order === 'table') return table

  if (chartType === 'clusteredColumn' || chartType === 'stackedColumn') {
    const columns = table.headers.slice(1).map((_, i) => i + 1)
    const key = chartType === 'stackedColumn'
      ? (c: number) => {
        let total: number | null = null
        for (const row of table.rows) {
          const v = toNumber(row[c])
          if (isNum(v)) total = (total ?? 0) + v
        }
        return total
      }
      : (c: number) => (table.rows[0] ? toNumber(table.rows[0][c]) : null)
    const ordered = orderRows(columns, key, order)
    return {
      ...table,
      headers: [table.headers[0], ...ordered.map((c) => table.headers[c])],
      rows: table.rows.map((row) => [row[0], ...ordered.map((c) => row[c])]),
    }
  }

  const rows = chartType === 'stackedBar'
    ? orderRows(table.rows, rowTotal, order)
    : orderRows(table.rows, 1, order)
  return { ...table, rows }
}
